/**
 * PasswordInput — drop-in replacement for `<input type="password">` with a
 * built-in show/hide eye toggle. Useful because operators sometimes mis-type
 * complex passwords and want to verify what they entered before submitting.
 *
 * Usage:
 *   <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} />
 *
 * Accepts every prop a normal <input> would (className, placeholder, required,
 * minLength, autoComplete, data-testid, etc.). The trailing eye button has
 * data-testid={`${data-testid}-toggle`} so tests can drive it.
 */
import { useState, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";

const PasswordInput = forwardRef(function PasswordInput(
  { className = "", "data-testid": testId, ...props },
  ref,
) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <input
        {...props}
        ref={ref}
        type={shown ? "text" : "password"}
        data-testid={testId}
        // 2.5rem right padding makes room for the toggle so the eye never
        // overlaps the user's typing — match the existing luxe-input padding.
        className={`${className} pr-10`}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShown((v) => !v)}
        className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-[#8A8A8A] hover:text-[#D4AF37] transition-colors"
        aria-label={shown ? "Hide password" : "Show password"}
        title={shown ? "Hide password" : "Show password"}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        {shown ? (
          <EyeOff className="w-4 h-4" strokeWidth={1.5} />
        ) : (
          <Eye className="w-4 h-4" strokeWidth={1.5} />
        )}
      </button>
    </div>
  );
});

export default PasswordInput;
